import { boardRosterStatusSchema } from '@teo/types';
import { z } from 'zod';
import { displayId } from './common.js';
export const defaultBoardPositions = [
    'President',
    'Vice President',
    'Treasurer',
    'Secretary',
    'Director',
];
const associationId = displayId.describe('An association display id, such as OWNDR4T5Y6U8.');
const editableRosterId = displayId
    .optional()
    .describe('A scheduled roster to edit, such as BRSTH2J3K4M5. Omit it to edit the draft of the next roster.');
const boardMemberId = displayId.describe('A board member display id, such as BRMBH2J3K4M5.');
const clearedWhenOmitted = (what) => `The ${what} of the member. Omit it to clear it, so send the stored value to keep it.`;
export const boardMemberInputSchema = z.object({
    ownershipId: associationId,
    rosterId: editableRosterId,
    memberId: boardMemberId.optional().describe('The member to change. Omit it to add a seat.'),
    unitId: displayId.describe('The unit of the member, such as UNITK4M5N6P7. It must have a named owner; searchOwners lists them.'),
    position: z.string().trim().min(1).max(100).describe('The seat, such as President or Treasurer.'),
    sortOrder: z
        .number()
        .int()
        .min(0)
        .describe('Where the seat shows in the roster, from 0 at the top.'),
    firstName: z.string().trim().min(1).max(100).describe('The first name of the member.'),
    lastName: z.string().trim().min(1).max(100).describe('The last name of the member.'),
    email: z
        .string()
        .trim()
        .email()
        .max(255)
        .optional()
        .describe(clearedWhenOmitted('email address')),
    mobile: z.string().trim().max(20).optional().describe(clearedWhenOmitted('mobile number')),
});
export const boardMemberContactInputSchema = z.object({
    memberId: boardMemberId,
    email: z.string().trim().max(255).optional().describe(clearedWhenOmitted('email address')),
    mobile: z.string().trim().max(20).optional().describe(clearedWhenOmitted('mobile number')),
});
export const boardMemberOutputSchema = z.object({
    id: displayId,
    position: z.string(),
    sortOrder: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
    mobile: z.string().nullable(),
    unitId: displayId.nullable(),
    unitIdentifier: z.string().nullable(),
    propertyAddress: z.string().nullable(),
    userId: displayId.nullable(),
    invitePending: z.boolean(),
    invitedAt: z.date().nullable(),
    lastLoginAt: z.date().nullable(),
});
export const boardRosterOutputSchema = z.object({
    id: displayId,
    ownershipId: displayId,
    status: boardRosterStatusSchema,
    name: z.string().nullable(),
    effectiveDate: z.string(),
    activatedAt: z.date().nullable(),
    archivedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    members: z.array(boardMemberOutputSchema),
});
export const listBoardRostersSchema = z.object({
    ownershipId: displayId,
});
export const listBoardRostersOutputSchema = z.array(boardRosterOutputSchema);
export const activateCurrentBoardRosterSchema = z.object({
    ownershipId: displayId,
    rosterId: displayId.optional(),
    effectiveDate: z.string().date(),
});
export const removeBoardMemberSchema = z.object({
    ownershipId: associationId,
    rosterId: editableRosterId,
    memberId: boardMemberId,
});
export const deleteScheduledBoardRosterSchema = z.object({
    ownershipId: displayId,
    rosterId: displayId,
});
export const rosterIdSchema = z.object({
    rosterId: displayId,
});
export const ownershipIdSchema = z.object({
    ownershipId: displayId,
});
export const memberIdSchema = z.object({
    memberId: displayId,
});
export const ownerRosterSearchSchema = z.object({
    ownershipId: displayId,
    search: z
        .string()
        .trim()
        .max(100)
        .default('')
        .describe('Text to match against unit numbers, addresses, and owner names, emails and phones. Leave it empty to list units with an owner on file.'),
});
export const ownerRosterOptionSchema = z.object({
    unitId: displayId,
    unitIdentifier: z.string().nullable(),
    propertyAddress: z.string(),
    ownerNames: z.string().nullable(),
    ownerEmails: z.string().nullable(),
    ownerPhones: z.string().nullable(),
});
export const ownerRosterSearchOutputSchema = z.array(ownerRosterOptionSchema);
export const inviteBoardMemberResultSchema = z.object({
    memberId: displayId,
    email: z.string().nullable(),
    status: z.enum(['invited', 'linked', 'skipped', 'failed']),
    message: z.string(),
});
export const inviteAllBoardMembersOutputSchema = z.object({
    results: z.array(inviteBoardMemberResultSchema),
});
//# sourceMappingURL=boardMembers.js.map